import { useState } from "react";
import { motion } from "framer-motion";
import { FadeUp } from "./_shared";

const PATHWAYS = {
  optimization: {
    label: "Optimization Programs",
    eyebrow: "Non-Medical Coaching",
    cta: "Explore Programs",
    href: "#programs",
    nodes: [
      ["https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/p3gnmb7k_An_illustrated_figure_performing_progressive_muscle_relaxati.png", "Composition + metabolism"],
      ["https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/i5lclf1b_jRFFOsoFOzWuh8Rw%20%281%29.png", "Sleep + recovery"],
      ["https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/9hayeu73_athlete_in_motion_with_holographic_mirror_neuron_network_ove.png", "Cognition + performance"],
      ["https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/qt2l9hq8_YyPB0uust8QmSFp3.png", "Healthy aging"],
    ],
  },
  medical: {
    label: "Medical Care",
    eyebrow: "Physician-Directed",
    cta: "Explore Medical Care",
    href: "/medical-care",
    nodes: [
      ["https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/uihfi9t2_n8Oo7oN4SQM7DJDjAayH3%20%281%29.png", "Clinical evaluation"],
      ["https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/vn3kpgub_Bclecywo063QFGmEqVEZm.png", "Testing + diagnostics"],
      ["https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/54pjvyfn_qYfMmUIBagtd-ERi.webp", "Prescriptions + therapies"],
      ["https://static.prod-images.emergentagent.com/jobs/c8a65975-d751-4172-9e88-294ec65cc742/images/662942ca7518cb626bc2d2b2d4a68bc9acbc3069949471836852bb3eebb158a2.jpeg", "Clinician management"],
    ],
  },
};

export default function TwoPathways() {
  const [activeKey, setActiveKey] = useState("optimization");
  const active = PATHWAYS[activeKey];

  return (
    <section id="pathways" data-testid="pathways-section" className="py-24 md:py-32 bg-foreground text-background">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <FadeUp className="max-w-3xl">
          <div className="font-mono text-xs uppercase tracking-[0.18em] text-background/60"><span className="text-accent">§</span> Two Pathways</div>
          <h2 className="mt-5 font-display font-bold tracking-normal leading-[1.05] text-[clamp(1.9rem,4.5vw,3.25rem)] text-background">
            Two paths. <em className="not-italic text-accent">One intelligence layer.</em>
          </h2>
        </FadeUp>

        <FadeUp className="mt-10 border-2 border-background/25">
          <div className="grid grid-cols-2 border-b-2 border-background/25" role="group" aria-label="Select a care pathway">
            {Object.entries(PATHWAYS).map(([key, pathway]) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveKey(key)}
                aria-pressed={activeKey === key}
                data-testid={`pathway-tab-${key}`}
                className={`min-h-16 px-4 py-3 font-display text-sm md:text-base font-bold transition-colors ${
                  activeKey === key ? "bg-accent text-white" : "bg-background/[0.04] text-background hover:bg-background/[0.09]"
                }`}
              >
                {pathway.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[420px]">
            <div className="lg:col-span-8 grid grid-cols-2 gap-px bg-background/15">
              {active.nodes.map(([image, label], index) => (
                <motion.div
                  key={`${activeKey}-${label}`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.06 }}
                  data-testid={`pathway-node-${activeKey}-${index + 1}`}
                  className="relative aspect-square min-h-0 sm:aspect-[4/3] sm:min-h-40 overflow-hidden bg-foreground"
                >
                  <img
                    src={image}
                    alt={label}
                    data-testid={`pathway-image-${activeKey}-${index + 1}`}
                    className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 hover:scale-[1.03]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground via-foreground/20 to-transparent" aria-hidden />
                  <span className="absolute inset-x-0 bottom-0 p-4 md:p-5 font-display text-sm md:text-base font-bold text-white leading-snug">{label}</span>
                </motion.div>
              ))}
            </div>

            <div className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-background/20 p-7 md:p-9 flex flex-col justify-center">
              <div className="font-mono text-xs uppercase tracking-[0.16em] text-background/55">{active.eyebrow}</div>
              <h3 data-testid="pathway-active-title" className="mt-3 font-display text-2xl font-bold text-background">{active.label}</h3>
              <p data-testid="pathway-active-summary" className="mt-5 text-base text-background/70 leading-relaxed">CPI and progress stay connected.</p>
              <a href={active.href} data-testid={`pathway-${activeKey}-cta`} className="mt-8 iw-btn-red inline-flex w-fit items-center gap-2 px-6 py-3.5 text-xs">
                {active.cta} <span aria-hidden>→</span>
              </a>
              <a href="/quiz" data-testid="pathways-quiz-link" className="mt-5 font-mono text-xs uppercase tracking-[0.14em] text-background/70 hover:text-accent transition-colors">
                Not sure? Take the quiz →
              </a>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}